import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, ModalController, IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeResourceUrl, SafeHtml } from '@angular/platform-browser';
import { SecurityContext } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { marked } from 'marked';
import { Song } from '../../entities/song.model';
import { Playlist } from '../../entities/playlist.model';
import { SongService } from '../../services/song.service';
import { PlaylistService } from '../../services/playlist.service';
import { UserLookupService } from '../../services/user-lookup.service';
import { SubmitSongModalComponent } from '../../components/submit-song-modal/submit-song-modal.component';
import { CreatePlaylistModalComponent } from '../../components/create-playlist-modal/create-playlist-modal.component';
import { SongNotesModalComponent } from '../../components/song-notes-modal/song-notes-modal.component';
import { CommentService } from '../../services/comment.service';
import { SongComment } from '../../entities/comment.model';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { LyricsService } from '../../services/lyrics.service';
import { TranscriptService } from '../../services/transcript.service';
import { LrclibService } from '../../services/lrclib.service';

interface SpotifyTrack {
  id: string;
  name: string;
  artists: { name: string }[];
  album: { name: string; images: { url: string }[] };
  duration_ms: number;
}

@Component({
  selector: 'app-topic-playlist',
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, RouterModule, TranslateModule, CreatePlaylistModalComponent],
  templateUrl: './topic-playlist.page.html',
  styleUrls: ['./topic-playlist.page.scss'],
})
export class TopicPlaylistPage implements OnInit, OnDestroy {
  language = '';
  topic = '';
  playlistName = '';
  playlist: Playlist | null = null;
  songs: Song[] = [];
  filteredSongs: Song[] = [];
  searchQuery = '';
  loading = false;
  expandedSongId: string | null = null;
  userVotes: Record<string, 'up' | 'down'> = {};
  displayNames: Record<string, string> = {};
  currentUser: any = null;
  private voteTimestamps: Record<string, number[]> = {};
  private readonly RATE_LIMIT_MAX = 5;
  private readonly RATE_LIMIT_WINDOW_MS = 60_000;

  private embedUrlCache = new Map<string, SafeResourceUrl>();
  openNotesSong: Song | null = null;
  renderedNote: SafeHtml = '';
  openCommentsSong: Song | null = null;
  comments: SongComment[] = [];
  commentsLoading = false;
  newCommentText = '';
  editingCommentId: string | null = null;
  editingCommentText = '';

  // Lyrics panel state (desktop)
  openLyricsSong: Song | null = null;
  lyricsLoading = false;
  lyricsError = '';
  lyricsActiveSegmentIndex = -1;
  lyricsTranslations: string[] = [];
  lyricsTranslationsVisible = false;
  lyricsTranslating = false;
  private lyricsTranslationsLoaded = false;
  private lyricsPollInterval: any = null;
  private mainYtPlayer: any = null;
  private mainYtPlayerInitialised = false;
  private pendingLyricsSeek: number | null = null;

  // ── Spotify song discovery (search bar → add from Spotify) ────────────────
  spotifyResults: SpotifyTrack[] = [];
  spotifyLoading = false;
  private spotifySubject = new Subject<string>();
  private spotifySub!: Subscription;

  get isOwner(): boolean {
    return !!this.currentUser && !!this.playlist?.createdByUid &&
      this.playlist.createdByUid === this.currentUser.uid;
  }

  get canSubmit(): boolean {
    if (this.isOwner) return true;
    const collab = this.playlist?.collaboration ?? 'editor';
    return collab === 'editor' || collab === 'full_editor';
  }

  get canVote(): boolean {
    if (this.isOwner) return true;
    const collab = this.playlist?.collaboration ?? 'editor';
    return collab === 'commenter' || collab === 'editor' || collab === 'full_editor';
  }

  isOwnContribution(song: Song): boolean {
    return !!this.currentUser?.uid && !!song.submittedByUid && song.submittedByUid === this.currentUser.uid;
  }

  canRemoveSong(song: Song): boolean {
    if (this.isOwner) return true;

    const collab = this.playlist?.collaboration ?? 'editor';
    if (collab === 'full_editor') return true;

    // In editable mode, collaborators can only remove songs they submitted.
    return collab === 'editor' && !!this.currentUser?.uid && song.submittedByUid === this.currentUser.uid;
  }

  canEditSong(song: Song): boolean {
    return this.canRemoveSong(song);
  }

  get canComment(): boolean {
    if (!this.currentUser) return false;
    if (this.isOwner) return true;
    const collab = this.playlist?.collaboration ?? 'editor';
    return collab === 'commenter' || collab === 'editor' || collab === 'full_editor';
  }

  get lyricsHasSegments(): boolean {
    return !!this.openLyricsSong?.transcriptSegments?.length;
  }

  private buildRenderedNote(song: Song): SafeHtml {
    const raw = marked.parse(song.note || '') as string;
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, raw) ?? '';
    return this.sanitizer.bypassSecurityTrustHtml(sanitized);
  }

  private async openSongModal(song: Song, initialTab: 'notes' | 'comments' | 'lyrics') {
    const renderedNote = this.buildRenderedNote(song);
    const s = this.songs.find(s => s.id === song.id);
    const modal = await this.modalController.create({
      component: SongNotesModalComponent,
      componentProps: {
        song,
        renderedNote,
        initialTab,
        canComment: this.canComment,
        currentUserUid: this.currentUser?.uid ?? '',
        currentUserDisplayName: this.currentUser?.displayName ?? (this.currentUser?.email ?? ''),
        nativeLanguage: this.currentUser?.nativeLanguage ?? 'en',
        onCommentCountChange: (delta: number) => {
          if (s) s.commentCount = Math.max(0, (s.commentCount ?? 0) + delta);
        },
      },
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      cssClass: 'song-notes-modal',
    });
    await modal.present();
  }

  async openComments(song: Song) {
    if (window.innerWidth <= 768) {
      await this.openSongModal(song, 'comments');
    } else {
      // Close notes panel if open
      this.openNotesSong = null;
      this.renderedNote = '';
      this.openCommentsSong = song;
      this.newCommentText = '';
      await this.loadComments(song.id!);
      setTimeout(() => {
        document.querySelector('.song-card.comments-active')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }

  closeComments() {
    this.openCommentsSong = null;
    this.comments = [];
    this.newCommentText = '';
  }

  private async loadComments(songId: string) {
    this.commentsLoading = true;
    try {
      this.comments = await this.commentService.getComments(songId);
    } finally {
      this.commentsLoading = false;
    }
  }

  async submitComment() {
    const text = this.newCommentText.trim();
    if (!text || !this.currentUser || !this.openCommentsSong?.id) return;

    const displayName = this.currentUser.displayName || this.currentUser.email || 'User';
    await this.commentService.addComment(
      this.openCommentsSong.id,
      this.currentUser.uid,
      displayName,
      text
    );
    this.songService.incrementCommentCount(this.openCommentsSong.id).catch(console.error);
    // Update local count
    const song = this.songs.find(s => s.id === this.openCommentsSong?.id);
    if (song) song.commentCount = (song.commentCount ?? 0) + 1;

    this.newCommentText = '';
    await this.loadComments(this.openCommentsSong.id);
  }

  startEditComment(comment: SongComment) {
    this.editingCommentId = comment.id!;
    this.editingCommentText = comment.text;
  }

  cancelEditComment() {
    this.editingCommentId = null;
    this.editingCommentText = '';
  }

  async saveEditComment() {
    const text = this.editingCommentText.trim();
    if (!text || !this.editingCommentId || !this.openCommentsSong?.id) return;
    await this.commentService.updateComment(this.editingCommentId, text);
    this.editingCommentId = null;
    this.editingCommentText = '';
    await this.loadComments(this.openCommentsSong.id);
  }

  async deleteComment(comment: SongComment) {
    if (!this.openCommentsSong?.id) return;
    await this.commentService.deleteComment(comment.id!);
    this.songService.decrementCommentCount(this.openCommentsSong.id).catch(console.error);
    const song = this.songs.find(s => s.id === this.openCommentsSong?.id);
    if (song) song.commentCount = Math.max(0, (song.commentCount ?? 1) - 1);
    await this.loadComments(this.openCommentsSong.id);
  }

  get isPinned(): boolean {
    const pinned = this.currentUser?.pinnedPlaylists ?? [];
    return !!this.playlist?.id && pinned.includes(this.playlist.id);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private songService: SongService,
    private playlistService: PlaylistService,
    private userLookup: UserLookupService,
    private modalController: ModalController,
    private alertController: AlertController,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private userService: UserService,
    private translate: TranslateService,
    private commentService: CommentService,
    private lyricsService: LyricsService,
    private transcriptService: TranscriptService,
    private lrclibService: LrclibService,
    private http: HttpClient
  ) {}

  ngOnInit() {
    // Spotify discovery search — debounced 400 ms, piggybacks on searchQuery
    this.spotifySub = this.spotifySubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(q => {
        if (!q.trim() || !this.canSubmit) {
          this.spotifyLoading = false;
          return of([]);
        }
        return this.http.get<any>(
          `https://api.spotify.com/v1/search?q=${encodeURIComponent(q)}&type=track&limit=6`
        ).pipe(
          map(res => res.tracks?.items ?? []),
          catchError(() => of([]))
        );
      })
    ).subscribe(tracks => {
      this.spotifyResults = tracks;
      this.spotifyLoading = false;
    });

    this.authService.currentUser$.subscribe(async user => {
      const wasSignedOut = !this.currentUser;
      this.currentUser = user;
      // Auth can resolve after songs have already loaded — reload votes once we know who the user is
      if (user && wasSignedOut && this.songs.length > 0) {
        this.userVotes = await this.songService.getUserVotes(
          user.uid,
          this.songs.map(s => s.id).filter((id): id is string => !!id)
        );
      }
    });
    this.route.paramMap.subscribe(params => {
      this.language = params.get('language') || '';
      this.topic = params.get('topic') || '';
      this.loadPlaylistName();
      this.loadSongs();
    });
  }

  ngOnDestroy() {
    this.stopLyricsPolling();
    this.destroyMainPlayer();
    this.spotifySub?.unsubscribe();
  }

  // ── Lyrics panel (desktop) ─────────────────────────────────────────────────

  async openLyrics(song: Song) {
    if (window.innerWidth <= 768) {
      await this.openSongModal(song, 'lyrics');
      return;
    }
    // Close other panels
    this.openNotesSong = null;
    this.renderedNote = '';
    this.openCommentsSong = null;
    this.comments = [];

    // Reset state (force-clear lyricsLoading to unblock any pending fetch)
    this.lyricsLoading = false;
    this.stopLyricsPolling();
    this.lyricsActiveSegmentIndex = -1;
    this.lyricsTranslations = [];
    this.lyricsTranslationsVisible = false;
    this.lyricsTranslationsLoaded = false;
    this.lyricsError = '';

    this.openLyricsSong = song;
    if (song.id && this.expandedSongId !== song.id) {
      this.expandedSongId = song.id;
    }
    // Wrap the iframe for time-sync once it's in the DOM
    this.wrapIframeForLyrics(song.id!);
    await this.ensureLyricsSegments();

    setTimeout(() => {
      document.querySelector('.song-card.lyrics-active')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  closeLyrics() {
    this.stopLyricsPolling();
    this.openLyricsSong = null;
    this.lyricsActiveSegmentIndex = -1;
    this.lyricsTranslations = [];
    this.lyricsTranslationsVisible = false;
    this.lyricsTranslationsLoaded = false;
    this.lyricsError = '';
  }

  private async ensureLyricsSegments() {
    if (!this.openLyricsSong) return;
    if (this.openLyricsSong.transcriptSegments?.length || this.lyricsLoading) return;

    const songId = this.openLyricsSong.id;
    if (!songId) return;

    this.lyricsLoading = true;
    this.lyricsError = '';
    try {
      const fresh = await this.songService.getSong(songId);
      if (this.openLyricsSong?.id !== songId) return;

      if (fresh?.transcriptSegments?.length) {
        this.openLyricsSong = { ...this.openLyricsSong, transcriptSegments: fresh.transcriptSegments };
        const idx = this.songs.findIndex(s => s.id === songId);
        if (idx >= 0) this.songs[idx] = { ...this.songs[idx], transcriptSegments: fresh.transcriptSegments };
        return;
      }

      // ── Try lrclib first (no cloud-IP restrictions, no auth needed) ──────
      const lrclibSegments = await this.lrclibService.getSegments(
        this.openLyricsSong.title ?? '',
        this.openLyricsSong.channelName ?? ''
      );
      if (this.openLyricsSong?.id !== songId) return;

      if (lrclibSegments?.length) {
        this.openLyricsSong = { ...this.openLyricsSong, transcriptSegments: lrclibSegments };
        const idx = this.songs.findIndex(s => s.id === songId);
        if (idx >= 0) this.songs[idx] = { ...this.songs[idx], transcriptSegments: lrclibSegments };
        // Persist so future opens skip the fetch
        this.songService.saveSegments(songId, lrclibSegments).catch(console.error);
        return;
      }

      // ── Fall back to YouTube captions ─────────────────────────────────────
      try {
        const result = await this.transcriptService.fetchTranscript(
          songId,
          this.openLyricsSong.youtubeUrl,
          this.openLyricsSong.language
        );
        if (this.openLyricsSong?.id !== songId) return;

        if (result.segments?.length) {
          this.openLyricsSong = { ...this.openLyricsSong, transcriptSegments: result.segments };
          const idx = this.songs.findIndex(s => s.id === songId);
          if (idx >= 0) this.songs[idx] = { ...this.songs[idx], transcriptSegments: result.segments };
        } else {
          this.lyricsError = 'No captions available for this video.';
        }
      } catch (fetchErr: any) {
        if (this.openLyricsSong?.id !== songId) return;
        const msg: string = fetchErr?.message ?? '';
        if (msg.includes('not-found') || msg.includes('No captions')) {
          this.lyricsError = 'No captions available for this video.';
        } else if (msg.includes('permission-denied') || msg.includes('unauthenticated')) {
          this.lyricsError = 'Captions not fetched yet.';
        } else {
          this.lyricsError = 'Could not load captions. Try again later.';
        }
      }
    } finally {
      if (this.openLyricsSong?.id === songId) {
        this.lyricsLoading = false;
        this.startLyricsPollingIfNeeded();
      }
    }
  }

  lyricsFormatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  seekLyricsToTime(start: number) {
    if (!this.openLyricsSong?.id) return;

    // If the video isn't expanded, expand it and queue the seek
    if (this.expandedSongId !== this.openLyricsSong.id) {
      this.pendingLyricsSeek = start;
      this.expandedSongId = this.openLyricsSong.id;
      this.wrapIframeForLyrics(this.openLyricsSong.id);
      return;
    }

    // If player isn't wrapped yet, wrap and queue the seek
    if (!this.mainYtPlayer?.seekTo) {
      this.pendingLyricsSeek = start;
      this.wrapIframeForLyrics(this.openLyricsSong.id);
      return;
    }

    this.mainYtPlayer.seekTo(start, true);
    this.mainYtPlayer.playVideo?.();
  }

  async toggleLyricsTranslations() {
    this.lyricsTranslationsVisible = !this.lyricsTranslationsVisible;
    if (this.lyricsTranslationsVisible && !this.lyricsTranslationsLoaded) {
      await this.loadLyricsTranslations();
    }
  }

  private async loadLyricsTranslations() {
    const segs = this.openLyricsSong?.transcriptSegments;
    if (!segs?.length) return;

    const fromLang = this.openLyricsSong!.language || 'auto';
    const toLang = this.currentUser?.nativeLanguage ?? 'en';
    const langKey = `${fromLang}_${toLang}`;

    // Use cached translations if already on the song object
    const cached = this.openLyricsSong!.transcriptTranslations?.[langKey];
    if (cached?.length) {
      this.lyricsTranslations = cached;
      this.lyricsTranslationsLoaded = true;
      return;
    }

    this.lyricsTranslating = true;
    try {
      const lines = segs.map(s => s.text);
      const translations = await this.lyricsService.translateLines(lines, fromLang, toLang);
      this.lyricsTranslations = translations;
      this.lyricsTranslationsLoaded = true;

      // Persist to Firestore so future opens skip Gemini entirely
      if (this.openLyricsSong!.id) {
        this.songService.saveTranslations(this.openLyricsSong!.id, langKey, translations).catch(console.error);
        const existing = this.openLyricsSong!.transcriptTranslations ?? {};
        const updated = { ...existing, [langKey]: translations };
        this.openLyricsSong = { ...this.openLyricsSong!, transcriptTranslations: updated };
        const idx = this.songs.findIndex(s => s.id === this.openLyricsSong?.id);
        if (idx >= 0) this.songs[idx] = { ...this.songs[idx], transcriptTranslations: updated };
      }
    } catch (err) {
      console.error('Lyrics translation failed:', err);
      this.lyricsTranslationsVisible = false;
    } finally {
      this.lyricsTranslating = false;
    }
  }

  /**
   * Wraps the existing YouTube iframe with the IFrame Player API
   * so we can read currentTime for lyrics sync.
   */
  private wrapIframeForLyrics(songId: string) {
    this.destroyMainPlayer();
    // Wait for Angular to render the iframe into the DOM
    setTimeout(async () => {
      if (this.mainYtPlayerInitialised) return;
      const iframeId = this.getMainPlayerElementId(songId);
      const iframe = document.getElementById(iframeId);
      if (!iframe) return;

      await this.loadYouTubeApi();
      const YT = (window as any)['YT'];
      try {
        this.mainYtPlayer = new YT.Player(iframeId, {
          events: {
            onReady: () => {
              if (this.pendingLyricsSeek !== null) {
                this.mainYtPlayer.seekTo(this.pendingLyricsSeek, true);
                this.mainYtPlayer.playVideo?.();
                this.pendingLyricsSeek = null;
              }
              this.startLyricsPollingIfNeeded();
            },
            onStateChange: (e: any) => {
              if (e.data === YT.PlayerState.ENDED) {
                this.lyricsActiveSegmentIndex = -1;
              }
              this.startLyricsPollingIfNeeded();
            },
          },
        });
        this.mainYtPlayerInitialised = true;
      } catch (err) {
        console.error('Failed to wrap YouTube iframe:', err);
        this.mainYtPlayer = null;
        this.mainYtPlayerInitialised = false;
      }
    }, 300);
  }

  private loadYouTubeApi(): Promise<void> {
    if ((window as any)['YT']?.Player) return Promise.resolve();
    return new Promise(resolve => {
      const prev = (window as any)['onYouTubeIframeAPIReady'];
      (window as any)['onYouTubeIframeAPIReady'] = () => {
        if (prev) prev();
        resolve();
      };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    });
  }

  private startLyricsPolling() {
    if (this.lyricsPollInterval) return;
    this.lyricsPollInterval = setInterval(() => {
      const t = this.mainYtPlayer?.getCurrentTime?.();
      if (typeof t === 'number') this.updateLyricsActiveSegment(t);
    }, 250);
  }

  private stopLyricsPolling() {
    if (this.lyricsPollInterval) {
      clearInterval(this.lyricsPollInterval);
      this.lyricsPollInterval = null;
    }
  }

  private updateLyricsActiveSegment(currentTime: number) {
    const segs = this.openLyricsSong?.transcriptSegments;
    if (!segs?.length) return;

    let idx = -1;
    for (let i = 0; i < segs.length; i++) {
      if (segs[i].start <= currentTime) idx = i;
      else break;
    }

    if (idx !== this.lyricsActiveSegmentIndex) {
      this.lyricsActiveSegmentIndex = idx;
      setTimeout(() => {
        document.querySelector('.lyrics-panel-scroll .lyric-line.active')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    }
  }

  private startLyricsPollingIfNeeded() {
    const shouldPoll = !!this.openLyricsSong?.id
      && this.expandedSongId === this.openLyricsSong.id
      && !!this.mainYtPlayer;
    if (shouldPoll) {
      this.startLyricsPolling();
      return;
    }
    this.stopLyricsPolling();
  }

  private async loadPlaylistName() {
    const playlist = await this.playlistService.getPlaylistById(this.topic);
    this.playlist = playlist;
    this.playlistName = playlist?.name ?? this.topic;

    if (playlist?.visibility === 'private' && !this.isOwner) {
      // Navigate away first so the content is never visible, then show the message
      await this.router.navigate(['/grammar'], { replaceUrl: true });
      const alert = await this.alertController.create({
        header: this.translate.instant('PLAYLISTS.PRIVATE_TITLE'),
        message: this.translate.instant('PLAYLISTS.PRIVATE_MSG'),
        buttons: [this.translate.instant('PLAYLISTS.OK')]
      });
      await alert.present();
    }
  }

  loadSongs(event?: any) {
    this.loading = true;
    this.songService.getSongsByTopic(this.language, this.topic).subscribe({
      next: async (songs) => {
        this.songs = songs;
        this.applySearch();
        try {
          await this.loadUserVotes();
        } catch (e) {
          console.warn('Could not load vote state:', e);
        }
        this.loading = false;
        if (event) event.target.complete();
        const uids = songs.map(s => s.submittedByUid).filter((u): u is string => !!u);
        if (uids.length) {
          const resolved = await this.userLookup.resolveMany(uids);
          this.displayNames = { ...this.displayNames, ...resolved };
        }
      },
      error: () => {
        this.loading = false;
        if (event) event.target.complete();
      }
    });
  }

  onSearch(event: any) {
    this.searchQuery = event.detail.value ?? '';
    this.applySearch();
    // Also feed Spotify discovery if user can add songs
    if (this.canSubmit) {
      this.spotifyLoading = this.searchQuery.trim().length > 0;
      if (!this.searchQuery.trim()) this.spotifyResults = [];
      this.spotifySubject.next(this.searchQuery);
    }
  }

  /** Opens the submit modal pre-labelled with a Spotify track's name/artist (user still provides YouTube URL) */
  async openSubmitModalForTrack(track: SpotifyTrack) {
    if (!this.currentUser || !this.currentUser.email) {
      await this.openSubmitModal(); // triggers sign-in prompt
      return;
    }
    if (!this.currentUser.displayName) {
      await this.openSubmitModal(); // triggers username prompt
      return;
    }
    if (this.currentUser.uid && this.currentUser.displayName) {
      this.userLookup.writeUsername(this.currentUser.uid, this.currentUser.displayName).catch(console.error);
    }
    const modal = await this.modalController.create({
      component: SubmitSongModalComponent,
      componentProps: {
        language: this.language,
        topic: this.topic,
        submittedBy: this.currentUser.displayName,
        submittedByUid: this.currentUser.uid,
        songCount: this.songs.length,
        initialTitle: `${track.name} - ${this.spotifyArtistNames(track)}`,
      }
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.submitted) {
      this.loadSongs();
    }
  }

  spotifyAlbumThumb(track: SpotifyTrack): string {
    const imgs = track.album?.images ?? [];
    return (imgs[imgs.length - 1] ?? imgs[0])?.url ?? '';
  }

  spotifyArtistNames(track: SpotifyTrack): string {
    return track.artists.map(a => a.name).join(', ');
  }

  private applySearch() {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) {
      this.filteredSongs = this.songs;
      return;
    }
    this.filteredSongs = this.songs.filter(s =>
      (s.title && s.title.toLowerCase().includes(q)) ||
      (s.channelName && s.channelName.toLowerCase().includes(q)) ||
      (s.note && s.note.toLowerCase().includes(q))
    );
  }

  private async loadUserVotes() {
    if (!this.currentUser) {
      this.userVotes = {};
      return;
    }
    const songIds = this.songs.map(s => s.id).filter((id): id is string => !!id);
    this.userVotes = await this.songService.getUserVotes(this.currentUser.uid, songIds);
  }

  async vote(song: Song, field: 'upvotes' | 'downvotes') {
    if (!song.id) return;
    if (this.isOwnContribution(song)) return;

    if (!this.currentUser) {
      const alert = await this.alertController.create({
        header: this.translate.instant('PLAYLISTS.SIGN_IN_TITLE'),
        message: this.translate.instant('PLAYLISTS.SIGN_IN_VOTE_MSG'),
        buttons: [
          {
            text: this.translate.instant('PLAYLISTS.SIGN_UP'),
            handler: () => { this.router.navigate(['/signup']); }
          },
          { text: this.translate.instant('PLAYLISTS.OK'), role: 'cancel' }
        ]
      });
      await alert.present();
      return;
    }

    const newVote: 'up' | 'down' = field === 'upvotes' ? 'up' : 'down';
    const currentVote = this.userVotes[song.id];

    // Clicking the same button does nothing
    if (currentVote === newVote) return;

    // Rate-limit vote switches (not first votes)
    if (currentVote !== undefined && !this.withinRateLimit(song.id)) return;

    const fromField: 'upvotes' | 'downvotes' | null =
      currentVote === 'up' ? 'upvotes' : currentVote === 'down' ? 'downvotes' : null;

    // Update local counts immediately for responsiveness
    if (fromField === 'upvotes') {
      song.upvotes = Math.max(0, song.upvotes - 1);
      this.playlistService.decrementTotalLikes(this.topic).catch(console.error);
    } else if (fromField === 'downvotes') {
      song.downvotes = Math.max(0, song.downvotes - 1);
    }

    if (field === 'upvotes') {
      song.upvotes++;
      this.playlistService.incrementTotalLikes(this.topic).catch(console.error);
    } else {
      song.downvotes++;
    }

    this.userVotes[song.id] = newVote;
    this.songService.setUserVote(this.currentUser.uid, song.id, newVote).catch(console.error);

    if (fromField) {
      this.songService.switchVote(song.id, fromField, field).catch(console.error);
    } else {
      this.songService.voteSong(song.id, field).catch(console.error);
    }
  }

  private withinRateLimit(songId: string): boolean {
    const now = Date.now();
    const recent = (this.voteTimestamps[songId] || []).filter(
      t => now - t < this.RATE_LIMIT_WINDOW_MS
    );
    if (recent.length >= this.RATE_LIMIT_MAX) return false;
    this.voteTimestamps[songId] = [...recent, now];
    return true;
  }

  getThumbnailUrl(song: Song): string {
    if (song.thumbnailUrl) return song.thumbnailUrl;
    const videoId = this.extractVideoId(song.youtubeUrl);
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';
  }

  toggleExpand(songId: string) {
    if (this.expandedSongId === songId) {
      this.expandedSongId = null;
      this.destroyMainPlayer();
      return;
    }

    this.expandedSongId = songId;
    this.destroyMainPlayer();

    // If lyrics panel is open for this song, wrap the iframe for time-sync
    if (this.openLyricsSong?.id === songId) {
      this.wrapIframeForLyrics(songId);
    }
  }

  getEmbedUrl(youtubeUrl: string): SafeResourceUrl {
    if (!this.embedUrlCache.has(youtubeUrl)) {
      const videoId = this.extractVideoId(youtubeUrl);
      const embedUrl = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
      this.embedUrlCache.set(youtubeUrl, this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl));
    }
    return this.embedUrlCache.get(youtubeUrl)!;
  }

  private extractVideoId(url: string): string {
    const match = url.match(/(?:v=|youtu\.be\/|embed\/)([^&\n?#]+)/);
    return match ? match[1] : '';
  }

  getMainPlayerElementId(songId: string): string {
    return `main-player-${songId}`;
  }

  trackBySongId(_index: number, song: Song): string {
    return song.id ?? '';
  }

  private destroyMainPlayer() {
    this.stopLyricsPolling();
    // Don't call destroy() — it removes the iframe from the DOM which
    // conflicts with Angular's *ngIf lifecycle.  Just drop the reference.
    this.mainYtPlayer = null;
    this.mainYtPlayerInitialised = false;
  }

  async openSubmitModal() {
    if (!this.currentUser || !this.currentUser.email) {
      const alert = await this.alertController.create({
        header: this.translate.instant('PLAYLISTS.SIGN_IN_TITLE'),
        message: this.translate.instant('PLAYLISTS.SIGN_IN_SUBMIT_MSG'),
        buttons: [
          {
            text: this.translate.instant('PLAYLISTS.SIGN_UP'),
            handler: () => { this.router.navigate(['/signup']); }
          },
          { text: this.translate.instant('PLAYLISTS.OK'), role: 'cancel' }
        ]
      });
      await alert.present();
      return;
    }

    if (!this.currentUser.displayName) {
      const alert = await this.alertController.create({
        header: this.translate.instant('PLAYLISTS.USERNAME_TITLE'),
        message: this.translate.instant('PLAYLISTS.USERNAME_MSG'),
        buttons: [
          {
            text: this.translate.instant('PLAYLISTS.SIGN_UP'),
            handler: () => { this.router.navigate(['/signup']); }
          },
          { text: this.translate.instant('PLAYLISTS.OK'), role: 'cancel' }
        ]
      });
      await alert.present();
      return;
    }

    // Ensure usernames collection has this user's current display name
    if (this.currentUser.uid && this.currentUser.displayName) {
      this.userLookup.writeUsername(this.currentUser.uid, this.currentUser.displayName).catch(console.error);
    }

    const modal = await this.modalController.create({
      component: SubmitSongModalComponent,
      componentProps: {
        language: this.language,
        topic: this.topic,
        submittedBy: this.currentUser.displayName,
        submittedByUid: this.currentUser.uid,
        songCount: this.songs.length
      }
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.submitted) {
      this.loadSongs();
    }
  }

  async openNotes(song: Song) {
    if (window.innerWidth <= 768) {
      await this.openSongModal(song, 'notes');
    } else {
      // Close comments panel if open
      this.openCommentsSong = null;
      this.comments = [];
      this.openNotesSong = song;
      this.renderedNote = this.buildRenderedNote(song);
      // Scroll the active song card into view so it sits beside the notes panel
      setTimeout(() => {
        document.querySelector('.song-card.notes-active')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }

  closeNotes() {
    this.openNotesSong = null;
    this.renderedNote = '';
  }

  async editSong(song: Song) {
    if (!song.id) return;
    const modal = await this.modalController.create({
      component: SubmitSongModalComponent,
      componentProps: {
        editMode: true,
        songId: song.id,
        initialTitle: song.title || '',
        initialNote: song.note || '',
        initialUrl: song.youtubeUrl || '',
      }
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.updated) {
      this.loadSongs();
    }
  }

  async removeSong(song: Song) {
    if (!song.id) return;
    if (!this.canRemoveSong(song)) return;

    const alert = await this.alertController.create({
      header: this.translate.instant('PLAYLISTS.REMOVE_TITLE'),
      message: this.translate.instant('PLAYLISTS.REMOVE_MSG', { title: song.title || 'this song' }),
      buttons: [
        { text: this.translate.instant('PLAYLISTS.CANCEL'), role: 'cancel' },
        {
          text: this.translate.instant('PLAYLISTS.REMOVE'),
          role: 'destructive',
          handler: async () => {
            await this.songService.deleteSong(song.id!);
            this.playlistService.decrementSongCount(this.topic).catch(console.error);
            if (song.upvotes > 0) {
              this.playlistService.decrementTotalLikes(this.topic).catch(console.error);
            }
            this.songs = this.songs.filter(s => s.id !== song.id);
            this.applySearch();
          }
        }
      ]
    });
    await alert.present();
  }

  doRefresh(event: any) {
    this.loadSongs(event);
  }

  async openEditModal() {
    if (!this.playlist?.id) return;
    const modal = await this.modalController.create({
      component: CreatePlaylistModalComponent,
      componentProps: {
        editMode: true,
        playlistId: this.playlist.id,
        initialName: this.playlist.name,
        initialDescription: this.playlist.description || '',
        initialVisibility: this.playlist.visibility || 'public',
        initialCollaboration: this.playlist.collaboration || 'editor',
      }
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.submitted) {
      await this.loadPlaylistName();
    } else if (data?.deleted) {
      this.router.navigate(['/playlists'], { replaceUrl: true });
    }
  }

  async togglePin() {
    if (!this.currentUser) return;
    await this.userService.togglePinnedPlaylist(this.topic, !this.isPinned);
  }

  async sharePlaylist() {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for browsers that block clipboard API
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    const alert = await this.alertController.create({
      header: this.translate.instant('PLAYLISTS.LINK_COPIED_TITLE'),
      message: this.translate.instant('PLAYLISTS.LINK_COPIED_MSG'),
      buttons: [this.translate.instant('PLAYLISTS.OK')]
    });
    await alert.present();
  }
}
